import adze from "adze";
import { type LogLevel, useLogsStore } from "~/stores/logs";

/**
 * Extracts message and metadata from log arguments
 * Separates string/primitive messages from object metadata
 * Handles cases like:
 * - logger.debug("message")
 * - logger.debug("message", { ... })
 * - logger.debug("message", "more info", { metadata })
 * - logger.debug({ ... })
 */
function extractMessageAndMetadata(
	args: unknown[],
	namespace?: string
): {
	message: string;
	metadata?: Record<string, unknown>;
} {
	if (args.length === 0) {
		return { message: namespace ? `${namespace} ` : "" };
	}

	const messageParts: string[] = [];
	const metadataObjects: Record<string, unknown>[] = [];

	// Separate messages from metadata
	for (const arg of args) {
		if (arg === null || arg === undefined) {
			messageParts.push(String(arg));
		} else if (typeof arg === "object" && !Array.isArray(arg)) {
			// Objects are treated as metadata
			metadataObjects.push(arg as Record<string, unknown>);
		} else if (Array.isArray(arg)) {
			// Arrays are stringified as part of the message
			messageParts.push(JSON.stringify(arg));
		} else {
			// Primitives (string, number, boolean) are part of the message
			messageParts.push(String(arg));
		}
	}

	// Concatenate message parts
	const messageText = messageParts.join(" ");

	// Merge all metadata objects
	const mergedMetadata =
		metadataObjects.length > 0
			? Object.assign({}, ...metadataObjects)
			: undefined;

	// Format: [NAMESPACE] message
	const fullMessage = namespace
		? `${namespace} ${messageText}`.trim()
		: messageText;

	return {
		message: fullMessage,
		metadata: mergedMetadata,
	};
}

/**
 * Creates a wrapped log method that intercepts calls and adds them to the logs store
 */
function createWrappedLogMethod(
	// biome-ignore lint/suspicious/noExplicitAny: needed for flexible logger method signatures
	consoleMethod: (...args: any[]) => void,
	level: Exclude<LogLevel, "all">,
	namespace?: string
) {
	return (...args: unknown[]) => {
		// extract message and metadata
		const { message, metadata } = extractMessageAndMetadata(args, namespace);

		// if (process.env.NODE_ENV === "development") {
		consoleMethod(...args);
		// }

		// add to logs store
		useLogsStore.getState().addLog(level, message, metadata);
	};
}

/**
 * Creates a wrapped logger from an adze instance
 */
function createWrappedLogger(
	// biome-ignore lint/suspicious/noExplicitAny: adze namespace returns different types
	adzeInstance: any,
	namespace?: string
) {
	return {
		debug: createWrappedLogMethod(
			adzeInstance.debug.bind(adzeInstance),
			"debug",
			namespace
		),
		error: createWrappedLogMethod(
			adzeInstance.error.bind(adzeInstance),
			"error",
			namespace
		),
		info: createWrappedLogMethod(
			adzeInstance.info.bind(adzeInstance),
			"info",
			namespace
		),
		namespace: (name: string) => {
			// Don't call adzeInstance.namespace() - it accumulates on HMR
			// Our extractMessageAndMetadata handles the namespace prefix
			return createWrappedLogger(adzeInstance, name);
		},
		trace: createWrappedLogMethod(
			console.trace.bind(console),
			"trace",
			namespace
		),
		warn: createWrappedLogMethod(
			adzeInstance.warn.bind(adzeInstance),
			"warn",
			namespace
		),
	};
}

const wrappedLogger = createWrappedLogger(adze);

/**
 * Logger for this frontend application
 * All log methods are wrapped to add metadata to the logs store
 *
 * Messages are concatenated as strings, objects are treated as metadata.
 * Format in logs store: "[NAMESPACE] concatenated messages" with merged metadata
 *
 * @example
 * // Basic usage
 * logger.debug("User logged in", { userId: "123" });
 * // Store: message = "User logged in", metadata = { userId: "123" }
 *
 * // Multiple messages
 * logger.info("Processing item", 42, "completed", { status: "success" });
 * // Store: message = "Processing item 42 completed", metadata = { status: "success" }
 *
 * // With namespace
 * import { logger as globalLogger } from "~/utils/logger-v2";
 * const logger = globalLogger.namespace("[recording]");
 * logger.debug("Recording started", { id: "123", duration: 45 });
 * // Store: message = "[recording] Recording started", metadata = { id: "123", duration: 45 }
 *
 * // Multiple metadata objects get merged
 * logger.warn("Error occurred", { code: 500 }, { retry: true });
 * // Store: message = "Error occurred", metadata = { code: 500, retry: true }
 */
export const logger = wrappedLogger;
